import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WatchIcon from '@mui/icons-material/Watch';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';

import { useVma } from '../Allures/useVma.ts';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { type ZoneRow } from '../Construction/allureTable.ts';
import { estCoach, useMoi } from '../Auth/moi.ts';

// Distances de course disponibles : celles qui ont une allure dediee dans les
// parametres d'allures (cf AlluresEnum cote serveur), avec leur distance
// officielle.
type DistanceCourse = {
  code: string;        // code de la zone d'allure correspondante
  libelle: string;
  distanceKm: number;
};

const DISTANCES: DistanceCourse[] = [
  { code: '3K', libelle: '3 km', distanceKm: 3 },
  { code: '5K', libelle: '5 km', distanceKm: 5 },
  { code: '10K', libelle: '10 km', distanceKm: 10 },
  { code: 'HP', libelle: 'Semi-marathon', distanceKm: 21.0975 },
  { code: 'MP', libelle: 'Marathon', distanceKm: 42.195 },
];

const SEMI_KM = 21.0975;

const VMA_MIN = 8;
const VMA_MAX = 30;

// VMA propre a cette page (GET/PUT/DELETE /api/preference/vma-passage cote
// serveur) : elle n'affecte pas la VMA de reference des preferences.
function requeteVmaPassage(method: 'GET' | 'PUT' | 'DELETE', body?: number): Request {
  return new Request('/api/preference/vma-passage', {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

function lireVma(saisie: string): number {
  const v = parseFloat(saisie.replace(',', '.'));
  return isFinite(v) && v >= VMA_MIN && v <= VMA_MAX ? Math.round(v * 10) / 10 : NaN;
}

type PointPassage = { km: number; libelle: string; semi?: boolean; arrivee?: boolean };

// Points de passage : chaque km jusqu'a 10 km ; tous les 5 km au-dela (ou
// chaque km si demande), plus le semi pour le marathon, et l'arrivee.
function pointsDePassage(distance: DistanceCourse, chaqueKm: boolean): PointPassage[] {
  const pas = distance.distanceKm <= 10 || chaqueKm ? 1 : 5;
  const points: PointPassage[] = [];
  for (let km = pas; km < distance.distanceKm - 1e-9; km += pas) {
    points.push({ km, libelle: `${km} km` });
  }
  if (distance.code === 'MP') {
    points.push({ km: SEMI_KM, libelle: 'Semi (21,1 km)', semi: true });
  }
  points.push({ km: distance.distanceKm, libelle: `Arrivée (${formaterKm(distance.distanceKm)})`, arrivee: true });
  return points.sort((a, b) => a.km - b.km);
}

function formaterKm(km: number): string {
  return `${(Math.round(km * 1000) / 1000).toLocaleString('fr-FR')} km`;
}

// Temps (secondes) -> 3h31'05" ou 18'45"
function formaterTemps(secondes: number): string {
  const total = Math.round(secondes);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}'${ss}"` : `${m}'${ss}"`;
}

// Allure (secondes par km) -> 4'05"/km
function formaterAllure(secondesParKm: number): string {
  const total = Math.round(secondesParKm);
  return `${Math.floor(total / 60)}'${String(total % 60).padStart(2, '0')}"/km`;
}

/**
 * Temps de passage pour une distance de course, a chacune des allures de
 * l'utilisateur (VMA de reference x %VMA de chaque zone de ses parametres
 * d'allures).
 */
export default function TempsPassage() {
  // Zones d'allure : reglees par le coach, lien vers les preferences pour lui seul.
  const moi = useMoi();
  // VMA de reference (preferences), et VMA ajustee pour cette page seulement.
  const [vmaReference] = useVma();
  const { table } = useAllureTable();
  const [vmaPassage, setVmaPassage] = React.useState<number | null>(null); // enregistree
  const [saisie, setSaisie] = React.useState<string>('');
  const [saisieModifiee, setSaisieModifiee] = React.useState(false);
  const [chargee, setChargee] = React.useState(false);
  const [etat, setEtat] = React.useState<'' | 'enregistrement' | 'enregistree' | 'erreur'>('');

  // Chargement de la VMA ajustee (404 = aucune : on part de la VMA de reference).
  React.useEffect(() => {
    fetch(requeteVmaPassage('GET'))
      .then(async (response) => {
        if (response.status === 200) {
          const v = (await response.json()) as number;
          setVmaPassage(v);
          setSaisie(String(v));
          setSaisieModifiee(true);
        }
      })
      .catch((e) => console.warn('VMA des temps de passage indisponible', e))
      .finally(() => setChargee(true));
  }, []);

  // Sans VMA ajustee, le champ affiche la VMA de reference.
  React.useEffect(() => {
    if (chargee && !saisieModifiee && isFinite(vmaReference) && vmaReference > 0) {
      setSaisie(String(vmaReference));
    }
  }, [chargee, saisieModifiee, vmaReference]);

  const vmaSaisie = lireVma(saisie);
  const saisieValide = !isNaN(vmaSaisie);
  const vma = saisieValide ? vmaSaisie : (vmaPassage ?? vmaReference);

  // Enregistrement automatique (apres une courte pause de frappe) de la VMA
  // ajustee, quand elle differe de celle deja enregistree.
  React.useEffect(() => {
    if (!saisieModifiee || !saisieValide || vmaSaisie === vmaPassage) return;
    if (vmaPassage === null && vmaSaisie === Math.round(vmaReference * 10) / 10) return;
    const minuteur = window.setTimeout(() => {
      setEtat('enregistrement');
      fetch(requeteVmaPassage('PUT', vmaSaisie))
        .then(async (response) => {
          if (response.status !== 200) throw new Error('statut ' + response.status);
          setVmaPassage((await response.json()) as number);
          setEtat('enregistree');
        })
        .catch(() => setEtat('erreur'));
    }, 700);
    return () => window.clearTimeout(minuteur);
  }, [saisieModifiee, saisieValide, vmaSaisie, vmaPassage, vmaReference]);

  // Retour a la VMA des preferences : la VMA ajustee est oubliee cote serveur.
  const reinitialiser = () => {
    fetch(requeteVmaPassage('DELETE'))
      .then((response) => {
        if (response.status !== 204) throw new Error('statut ' + response.status);
        setVmaPassage(null);
        setSaisieModifiee(false);
        setSaisie(String(vmaReference));
        setEtat('');
      })
      .catch(() => setEtat('erreur'));
  };

  const vmaAjustee = vmaPassage !== null || (saisieValide && vmaSaisie !== Math.round(vmaReference * 10) / 10);
  const [distance, setDistance] = React.useState<DistanceCourse>(DISTANCES[2]);
  const [chaqueKm, setChaqueKm] = React.useState(false);

  // Zones d'allure utiles en course : toutes sauf la recuperation.
  const zones = React.useMemo<ZoneRow[]>(
    () => table.filter((z: ZoneRow) => z.code !== 'RECUP' && isFinite(z.pctVMA) && z.pctVMA > 0),
    [table],
  );

  // Allure de chaque zone, en secondes par km : 3600 / (VMA x %VMA).
  const allures = React.useMemo(
    () => zones.map((z) => ({ zone: z, secondesParKm: 3600 / (vma * z.pctVMA) })),
    [zones, vma],
  );

  const points = React.useMemo(() => pointsDePassage(distance, chaqueKm), [distance, chaqueKm]);
  const vmaValide = isFinite(vma) && vma > 0;

  return (
    <Box component="main" sx={{ p: 12, pt: 10 }}>

      <Typography sx={{ mb: 3 }} color="text.secondary">
        Choisis une distance de course pour voir tes temps de passage à chacune de tes allures, calculées à partir de
        la VMA ci-dessous et de tes {estCoach(moi) ? <Link to="/preferences?onglet=allures">paramètres d'allures</Link> : "zones d'allure"}.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="VMA pour les passages (km/h)"
          type="number"
          size="small"
          value={saisie}
          onChange={(e) => {
            setSaisie(e.target.value);
            setSaisieModifiee(true);
            setEtat('');
          }}
          error={saisie !== '' && !saisieValide}
          helperText={saisie !== '' && !saisieValide ? `Entre ${VMA_MIN} et ${VMA_MAX} km/h` : undefined}
          inputProps={{ step: 0.1, min: VMA_MIN, max: VMA_MAX }}
          sx={{ width: 220 }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {vmaAjustee ? (
            <Chip size="small" color="secondary" label="VMA ajustée pour cette page" />
          ) : (
            // Meme libelle et meme couleur que sur la page Allures (historique).
            <Chip size="small" color="primary" label="VMA de référence" />
          )}
          <Typography variant="caption" color="text.secondary">
            {etat === 'enregistrement' && 'Enregistrement…'}
            {etat === 'enregistree' && 'Enregistrée — utilisée uniquement pour les temps de passage'}
            {etat === 'erreur' && 'Enregistrement impossible'}
            {etat === '' && (isFinite(vmaReference) && vmaReference > 0
              ? `VMA de référence : ${vmaReference.toLocaleString('fr-FR')} km/h`
              : '')}
          </Typography>
        </Box>
        <Button
          startIcon={<RestartAltIcon />}
          onClick={reinitialiser}
          disabled={!vmaAjustee || !(isFinite(vmaReference) && vmaReference > 0)}
          sx={{ ml: 'auto' }}
        >
          Revenir à la VMA de référence
          {isFinite(vmaReference) && vmaReference > 0 ? ` (${vmaReference.toLocaleString('fr-FR')} km/h)` : ''}
        </Button>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mb: 3 }}>
        <ToggleButtonGroup
          exclusive
          color="primary"
          value={distance.code}
          onChange={(_e, code: string | null) => {
            const choisie = DISTANCES.find((d) => d.code === code);
            if (choisie) setDistance(choisie);
          }}
          aria-label="Distance de course"
        >
          {DISTANCES.map((d) => (
            <ToggleButton key={d.code} value={d.code} sx={{ px: 2.5, textTransform: 'none' }}>
              {d.libelle}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {distance.distanceKm > 10 && (
          <FormControlLabel
            control={<Switch checked={chaqueKm} onChange={(e) => setChaqueKm(e.target.checked)} />}
            label="Afficher chaque kilomètre"
          />
        )}

        {vmaValide && (
          <Button
            component={Link}
            to={`/bracelet?distance=${distance.code}&vma=${vma}`}
            variant="outlined"
            startIcon={<WatchIcon />}
            sx={{ ml: 'auto' }}
          >
            Créer un bracelet
          </Button>
        )}
      </Box>

      {!vmaValide ? (
        <Typography color="text.secondary">
          Aucune VMA disponible : saisis un record sur la page <Link to="/allures">Allures</Link>.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader size="small" aria-label={`Temps de passage - ${distance.libelle}`}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Passage</TableCell>
                {allures.map(({ zone, secondesParKm }) => {
                  const cible = zone.code === distance.code;
                  return (
                    <TableCell
                      key={zone.code}
                      align="right"
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        ...(cible && { bgcolor: 'primary.main', color: 'primary.contrastText' }),
                      }}
                    >
                      {zone.nom}
                      <Box component="div" sx={{ fontWeight: 400, fontSize: '0.75rem', opacity: cible ? 0.9 : 0.7 }}>
                        {formaterAllure(secondesParKm)} · {Math.round(zone.pctVMA * 1000) / 10} %
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {points.map((point) => (
                <TableRow
                  key={point.libelle}
                  hover
                  sx={{
                    ...(point.semi && { bgcolor: 'action.selected' }),
                    ...(point.arrivee && { '& td': { fontWeight: 700, borderTop: 2, borderTopColor: 'divider' } }),
                  }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: point.semi ? 600 : undefined }}>
                    {point.libelle}
                    {point.semi && <Chip label="mi-course" size="small" sx={{ ml: 1 }} />}
                  </TableCell>
                  {allures.map(({ zone, secondesParKm }) => {
                    const cible = zone.code === distance.code;
                    return (
                      <TableCell
                        key={zone.code}
                        align="right"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          whiteSpace: 'nowrap',
                          ...(cible && { bgcolor: 'action.hover', fontWeight: 600 }),
                        }}
                      >
                        {formaterTemps(secondesParKm * point.km)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {vmaValide && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          La colonne mise en évidence correspond à l'allure spécifique de la distance choisie.
        </Typography>
      )}
    </Box>
  );
}
